import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { useOrariNegozio } from '../../../hooks/useOrariNegozio';
import {
  GiornoFascia,
  PresenzeGiorni,
  usePresenzeGiorni,
} from '../../../hooks/usePresenzeGiorni';
import { FasceOrarie } from '../../../utility_objects/FasceOrarieUtils';
import { getGiorniVisibili } from '../../../utility_objects/PresenzeUtils';

export interface GiornoFasciaNegozio extends GiornoFascia {
  /**
   * Fasce del punto vendita in quel giorno, per «Orario negozio»; null = negozio
   * chiuso, orari non ancora arrivati o in errore, o fascia in «Presenze».
   */
  orarioNegozio: FasceOrarie | null;
}

export interface PresenzeFascia extends PresenzeGiorni {
  giorni: GiornoFasciaNegozio[];
  /**
   * Fascia da disegnare: una modalità scelta («Presenze» o «Presenze totali»), un
   * punto vendita e fuori dal Mese.
   */
  visibile: boolean;
}

/**
 * Dati della fascia Presenze dell'agenda negozio: i giorni visibili nella vista e
 * nel punto vendita selezionati, letti da usePresenzeGiorni, uguali nelle due
 * modalità (passare dall'una all'altra non rilegge nulla). In «Presenze totali»
 * ogni giorno porta anche le fasce del negozio (useOrariNegozio, con gli stessi
 * parametri del calendario). Senza fascia, senza punto vendita o nel Mese non parte
 * nessuna chiamata; finché i filtri non sono caricati le celle restano in
 * caricamento.
 */
export const usePresenzeFascia = (): PresenzeFascia => {
  const {
    view,
    selectedDate,
    selectedPointOfSale,
    selectedUsers,
    presenzeModo,
  } = useSelector((state: RootState) => state.bluBookingDefaultCalendar);
  const { filtersLoad } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );
  const pointOfSaleId = selectedPointOfSale?.id ?? '';
  const visibile =
    presenzeModo !== 'nessuna' && view !== 'Month' && !!pointOfSaleId;

  // La data selezionata può arrivare come Date o come stringa: conta solo il giorno.
  const giornoSelezionato = dayjs(selectedDate).format('YYYY-MM-DD');
  const giorni = useMemo(
    () => (visibile ? getGiorniVisibili(view, giornoSelezionato) : []),
    [visibile, view, giornoSelezionato]
  );

  const presenze = usePresenzeGiorni({
    pointOfSaleId,
    giorni,
    attiva: visibile && filtersLoad,
  });
  const conOrari = visibile && presenzeModo === 'totali' && !!filtersLoad;
  // Gli stessi parametri del calendario (CalendarDefaultInner): stessa chiave.
  const orariNegozio = useOrariNegozio({
    pointOfSaleId,
    selectedDate,
    view,
    selectedUsers,
    attiva: conOrari,
  });
  // Spenta, la query restituisce comunque gli orari che il calendario ha letto con
  // la stessa chiave: in «Presenze» non servono, e i giorni non cambiano per loro.
  const orari = conOrari ? orariNegozio : undefined;

  return useMemo(
    () => ({
      ...presenze,
      giorni: presenze.giorni.map((giorno) => ({
        ...giorno,
        orarioNegozio: orari?.[giorno.giorno] ?? null,
      })),
      visibile,
    }),
    [presenze, orari, visibile]
  );
};
